import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { href, Link } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  Alert,
  Box,
  Button,
  Field,
  Flex,
  Input,
  PasswordInput,
  Text,
} from '@valwin/facto/components';

import { useErrorTranslator } from '~shared/i18n';
import { ROUTES, usePharmacyId } from '~shared/routes';

import {
  type LoginFormData,
  LoginFormErrors,
  loginSchema,
} from '../model/login.schema';
import { usePostLogin } from '../model/usePostLogin';

export function LoginForm() {
  const pharmacyId = usePharmacyId();

  const { t } = useTranslation();

  const { login, reset, isError, isPending } = usePostLogin(pharmacyId);

  const translateError = useErrorTranslator<LoginFormErrors>({
    USERNAME_REQUIRED: () => t('auth identifierRequired'),
    PASSWORD_REQUIRED: () => t('auth passwordRequired'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = handleSubmit(async (data) => {
    login(data);
  });

  const handleInputChange = () => {
    if (isError) {
      reset();
    }
  };

  return (
    <Flex flexDirection="column" gap="8" width="full">
      <Text textStyle="sm" color="gray.500" textAlign="center" marginBottom={0}>
        {t('auth loginInstructions')}
      </Text>
      <form onSubmit={onSubmit} noValidate>
        <Box display="flex" flexDirection="column" gap={5}>
          <Field.Root required invalid={!!errors.username?.message}>
            <Field.Label>
              {t('auth identifier')} <Field.RequiredIndicator />
            </Field.Label>
            <Input
              placeholder={t('auth identifierPlaceholder')}
              {...register('username', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.username?.message &&
                translateError(errors.username.message)}
            </Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!errors.password?.message}>
            <Field.Label>
              {t('auth password')} <Field.RequiredIndicator />
            </Field.Label>
            <PasswordInput
              placeholder={t('auth passwordPlaceholder')}
              {...register('password', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.password?.message &&
                translateError(errors.password.message)}
            </Field.ErrorText>
            <Field.HelperText
              alignSelf="end"
              color="brand.solid"
              _hover={{ textDecoration: 'underline' }}
            >
              <Link to={href(ROUTES.auth.reset, { pharmacyId })}>
                {t('auth forgotPassword')}
              </Link>
            </Field.HelperText>
          </Field.Root>

          {isError && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Title>{t('auth invalidCredentials')}</Alert.Title>
            </Alert.Root>
          )}

          <Flex direction="row" justifyContent="flex-end" marginTop={4}>
            <Button
              type="submit"
              size="sm"
              disabled={
                isSubmitting || Object.values(errors).length > 0 || isPending
              }
              flex={{ base: '1 0 100%', md: '1' }}
            >
              {t('auth connect')}
            </Button>
          </Flex>
        </Box>
      </form>
    </Flex>
  );
}
